import ControllerBase from '../../../../global/js/ControllerBase';
import { fromEvent } from 'rxjs';
import { AtomFabButtonEvents } from 'wolf/components/atoms/a-fab-button/a-fab-button.events';
import { MoleculeFabMenuListProps } from 'wolf/components/molecules/m-fab-menu-list/m-fab-menu-list.data';
import { MoleculeFabDrawerProps } from 'wolf/components/molecules/m-fab-drawer/m-fab-drawer.data';
import { Component } from 'global/interfaces/component.interface';
import { OrganismFabMenuEvents, OrganismFabMenuEventTypes } from './o-fab-menu.events';

const drawerTemplate = require('components/molecules/m-fab-drawer/m-fab-drawer.template.twig') as (
	data: Component<MoleculeFabDrawerProps>,
) => string;


interface ComponentRefs {
	fabMenu: 'fabMenu',
	/** The close menu button */
	closeMenu: 'closeMenu',
	/** The close menu button inside the drawer */
	closeMenuFromDrawer: 'closeMenuFromDrawer',
	/** The drawer overlay */
	drawer: 'drawer',
	/** The drawer content render space */
	drawerContent: 'drawerContent',
	/** The cross to close the drawer overlay */
	closeDrawer: 'closeDrawer',

	/** Watch and adjust position */
	premiumObserver: 'premiumObserver'
}

export default class ControllerFabMenu extends ControllerBase<ComponentRefs> {
	private readonly componentName = 'o-fab-menu';

	private readonly drawer: HTMLDivElement;

	constructor(element: HTMLElement) {
		super(element);

		this.drawer = this.$references.drawer as HTMLDivElement;
		this.observeFabMenuTrigger();
		this.observeBackdropClicks();
		this.observeCloseMenuClick();
		this.observeMenuLinkClicks();
		this.observeDrawerCloseClick();
		this.observeFabMenuPosition();
	}

	private observeFabMenuPosition(): void {

		const stickyButton = document.querySelector('#o-fab-menu-button') as HTMLElement;
		const fabButton = document.querySelector(".a-fab-button__anchor") as HTMLElement;
		const footer = document.querySelector('#footer') as HTMLElement;
		const style = stickyButton.style as CSSStyleDeclaration;
		let RIGHT_POSITION: number, BOTTOM_POSITION: number;

		const windowHandler = () => {
			if (window.innerWidth < 768) {
				RIGHT_POSITION = 15;
				BOTTOM_POSITION = 15;
			} else {
				RIGHT_POSITION = 30;
				BOTTOM_POSITION = 30;
			}
		}
		window.addEventListener("DOMContentLoaded", windowHandler, false);
		window.addEventListener("resize", windowHandler, false);

		function checkOffset() {
			const fabButton = document.querySelector(".a-fab-button__anchor") as HTMLElement;
			const menuIsOpen = fabButton?.classList.contains("open") as boolean;
			const screenBottom = window.scrollY + window.innerHeight as number;
			style.position = 'fixed';
			style.right = `${RIGHT_POSITION}px`;
			style.transition = "all 6ms ease-out"
			if (footer.offsetTop > screenBottom || menuIsOpen) style.bottom = `${BOTTOM_POSITION}px`;
			else style.bottom = `${screenBottom - footer.offsetTop + BOTTOM_POSITION}px`;
		}
		window.addEventListener("scroll", (): void => checkOffset());

		const checkIfOpen = (): void => {
			const menuIsOpen = fabButton?.classList.contains("open") as boolean;
			const screenBottom = window.scrollY + window.innerHeight as number;
			setTimeout(() => {
				stickyButton.style.position = 'fixed';
				stickyButton.style.transition = "all 0.25s ease-out";
				stickyButton.style.right = `${RIGHT_POSITION}px`;
				if (menuIsOpen) stickyButton.style.bottom = `${BOTTOM_POSITION}px`;
				else if (footer.offsetTop > screenBottom && !menuIsOpen) style.bottom = `${BOTTOM_POSITION}px`;
				else style.bottom = `${screenBottom - footer.offsetTop + BOTTOM_POSITION}px`;
			}, 200)
		}
		stickyButton.addEventListener("click", (): void => checkIfOpen(), false);
		this.$references.closeMenu.addEventListener("click", (): void => checkIfOpen(), false);
		this.$element.addEventListener("click", (): void => checkIfOpen(), false);
	}

	/**
	 * Toggle the menu and emit an event about the new menu open state
	 * @returns void
	 */
	private toggleMenu(): void {
		const openClass = 'open';
		this.$element.classList.toggle(openClass);
		const menuIsOpen = this.$element.classList.contains(openClass);
		this.dispatchCustomEvent<OrganismFabMenuEventTypes>(OrganismFabMenuEvents['menuIsOpen'], { menuIsOpen }, true, true);

		if (menuIsOpen) {
			this.$element.style.animation = 'fadeIn 0.25s ease-out 0.5s forwards';
		} else {
			this.$element.style.animation = 'fadeOut 0.25s ease-out 0s forwards';
		}
	}

	/**
	 * Listen for clicks on the fab button atom
	 */
	private observeFabMenuTrigger(): void {

		const click$ = fromEvent(window, AtomFabButtonEvents.buttonClicked);

		click$.subscribe((e: Event | MouseEvent) => {
			this.toggleMenu();
		});
	}

	/**
	 * Close Fab menu when click outside of menu
	 */
	private observeBackdropClicks(): void {
		const inner = document.querySelector(".o-fab-menu__inner");
		const header = document.querySelector(".o-fab-menu__header-wrapper");
		const body = document.querySelector(".o-fab-menu__body");
		const headline = document.querySelector("h3");

		fromEvent(this.$element, 'click').subscribe((e: Event | MouseEvent) => {
			if (e.target !== inner && e.target !== header && e.target !== body && e.target !== headline) {
				this.toggleMenu();
			}
		});
	}

	/**
	 * Listen for the user closing the menu using either button
	 * @returns void
	 */
	private observeCloseMenuClick(): void {
		this.$references.closeMenu && fromEvent(this.$references.closeMenu, 'click').subscribe((e: Event | MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			this.toggleMenu();
		});

		this.$references.closeMenuFromDrawer && fromEvent(this.$references.closeMenuFromDrawer, 'click').subscribe((e: Event | MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			this.toggleMenu();
		});

	}

	/**
	 * Listen for clicks on special menu items that target the opening of a drawer
	 * @returns void
	 */
	private observeMenuLinkClicks(): void {
		const menuLinks = this.$element.querySelectorAll<HTMLAnchorElement>([
			'.',
			this.componentName,
			'__body a.js-listitem',
		].join(''));

		menuLinks.length && menuLinks.forEach((link, idx) => {
			if (link.getAttribute('data-drawer') !== null) {
				link.addEventListener('click', e => this.handleOpenDrawerClick(e, idx));
			}
		});
	}

	/**
	 * Listen to clicks on menu items which should toggle the drawer and render their content
	 * @param  {Event|MouseEvent} event
	 * @param  {number} linkIndex
	 * @returns void
	 */
	private handleOpenDrawerClick(event: Event | MouseEvent, linkIndex: number): void {
		event.preventDefault();
		event.stopPropagation();

		this.toggleDrawer().then((drawIsOpen: boolean) => {

			// @todo - abstract this?
			// the drawer content is json encoded onto the molecule 'closest' to each anchor!
			const drawerContentRef: ComponentRefs['drawerContent'] = 'drawerContent';
			// find the molecule div
			const molecule: HTMLDivElement | null = (event.currentTarget as HTMLAnchorElement).closest<HTMLDivElement>('.m-fab-menu-list');
			// get the data for this molecule and parse
			const moleculeData: MoleculeFabMenuListProps | undefined = molecule?.dataset.data && JSON.parse(molecule.dataset.data);

			if (moleculeData) {
				// get the data related to this link
				const drawerData = moleculeData.links.length > linkIndex ? moleculeData.links[linkIndex].content : undefined;
				if (!drawerData) return;
				// build twig data
				const newDraweHTML = drawerTemplate({
					data: drawerData,
				});
				// render the twig template onto the drawer
				this.domUtils.renderDomElement(drawerContentRef, newDraweHTML);
			}
		});
	}

	/**
	 * Toggle the 2nd level drawer and resolve the new open state of the drawer
	 * @returns Promise<boolean>
	 */
	private async toggleDrawer(): Promise<boolean> {
		if (this.drawer) {
			const overlayClass = 'overlay';
			const drawerIsOpen = this.drawer.classList.contains(overlayClass);

			this.drawer.classList.toggle(overlayClass);

			return Promise.resolve(drawerIsOpen);
		}
		return Promise.reject();
	}

	/**
	 * Listen to clicks on the drawer close icon (chevron) and toggle the drawer
	 * @returns void
	 */
	private observeDrawerCloseClick(): void {
		this.$references.closeDrawer &&
			fromEvent(this.$references.closeDrawer, 'click')
				.subscribe((e: Event | MouseEvent) => {

					e.preventDefault();
					e.stopPropagation();

					this.toggleDrawer();
				});
	}
}
